import { useMemo, useRef, useState } from 'react';
import { MailIcon } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Button,
  EmptyState,
  ErrorNotice,
  Field,
  PageHeading,
  Panel,
  Pill,
  SectionHeader,
  SelectInput,
  SkeletonTable,
  Tabs,
  TextArea,
  TextInput,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  FIRST_NAME_PLACEHOLDER,
  GMAIL_QUERY_KEY,
  GMAIL_STATUS,
  OWNER_PARAM,
  composeQueryKey,
  connectGmail,
  fillForClient,
  opensLabel,
  readComposeContext,
  readGmailConnection,
  sendOutreachEmail,
  type ComposeContext,
  type GmailConnection,
  type SentEmail,
} from '@/lib/outreach-email';
import {
  OUTREACH_QUERY_KEYS,
  OUTREACH_TEMPLATE_LIMITS,
  SIGNATURE_QUERY_KEY,
  insertAt,
  listMyTemplates,
  listPlaceholders,
  listTeamTemplates,
  placeholderToken,
  readSignature,
  type OutreachTemplate,
} from '@/lib/outreach-templates';
import { REPEAT_CLIENTS_PATH } from '@/lib/repeat-clients';
import { clearDraft, usePendingDraft, type PendingDraft } from '@/lib/outreach-email-draft';
import { REPEAT_CLIENTS_QUERY_KEY } from '@/pages/RepeatClientsPage';
import { PlaceholderPicker } from '@/components/outreach-templates/PlaceholderPicker';
import { TemplatePreview } from '@/components/outreach-templates/TemplatePreview';

const VIEW = { draft: 'draft', preview: 'preview' } as const;
type View = (typeof VIEW)[keyof typeof VIEW];

const TEXT_FIELD = { subject: 'subject', body: 'body' } as const;
type TextField = (typeof TEXT_FIELD)[keyof typeof TEXT_FIELD];
const TEXT_FIELD_NAME: Readonly<Record<TextField, string>> = { [TEXT_FIELD.subject]: 'subject', [TEXT_FIELD.body]: 'email' };

const SENT_AT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

/**
 * Repeat clients → Email a client: a full page. Pick who it goes to and a
 * template, write, preview with the client's own details and the rep's
 * signature, then send from the rep's Gmail. bcecloud logs it to Salesforce
 * and marks the client Contacted.
 *
 * @returns The page.
 */
export function OutreachEmailPage(): React.ReactElement {
  const { accountId = '' } = useParams();
  const [params] = useSearchParams();
  const ownerId = params.get(OWNER_PARAM) ?? undefined;
  const context = useQuery({
    queryKey: composeQueryKey(accountId, ownerId),
    queryFn: ({ signal }) => readComposeContext(accountId, ownerId, signal),
  });
  const gmail = useQuery({ queryKey: GMAIL_QUERY_KEY, queryFn: ({ signal }) => readGmailConnection(signal) });
  const mine = useQuery({ queryKey: OUTREACH_QUERY_KEYS.mine, queryFn: ({ signal }) => listMyTemplates(signal) });
  const team = useQuery({ queryKey: OUTREACH_QUERY_KEYS.team, queryFn: ({ signal }) => listTeamTemplates(signal) });
  const placeholders = useQuery({ queryKey: OUTREACH_QUERY_KEYS.placeholders, queryFn: ({ signal }) => listPlaceholders(signal), staleTime: Infinity });
  // A draft the assistant wrote for this client; a new one starts the form over.
  const draft = usePendingDraft(accountId);

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="REPEAT CLIENTS"
        backTo={REPEAT_CLIENTS_PATH}
        title={context.data ? `Email ${context.data.accountName || 'this client'}` : 'Email a client'}
        subtitle="Sent from your Gmail, logged to Salesforce as yours, and the client moves to Contacted."
      />
      {context.isError ? (
        <ErrorNotice title="This client can’t be emailed from here" message={errorMessage(context.error)} />
      ) : context.isPending || gmail.isPending || mine.isPending || team.isPending ? (
        <SkeletonTable label="Loading the client…" />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-4">
            {gmail.data ? <GmailStep connection={gmail.data} /> : null}
            <ComposeForm
              key={draft?.nonce ?? 'blank'}
              draft={draft}
              context={context.data}
              ownerId={ownerId}
              templates={[...(mine.data ?? []), ...(team.data?.templates ?? [])]}
              placeholderLabels={placeholders.data ?? []}
              canSend={gmail.data?.status === GMAIL_STATUS.connected}
              from={gmail.data?.email ?? null}
            />
          </div>
          <History emails={context.data.history} />
        </div>
      )}
    </section>
  );
}

/** Connect (or reconnect) Gmail before the first send. */
function GmailStep({ connection }: { readonly connection: GmailConnection }): React.ReactElement | null {
  const connect = useMutation({ mutationFn: connectGmail, onError: (error) => toast.error(errorMessage(error)) });
  if (connection.status === GMAIL_STATUS.connected) return null;
  if (!connection.configured) {
    return <ErrorNotice title="Gmail sending isn’t set up yet" message="Ask an admin to finish the Google setup. You can still write the email." />;
  }
  const reconnect = connection.status === GMAIL_STATUS.needsReconnect;
  return (
    <Panel>
      <EmptyState
        icon={<MailIcon aria-hidden className="size-6" />}
        title={reconnect ? 'Reconnect your Gmail' : 'Connect your Gmail to send'}
        message={
          reconnect
            ? 'Google stopped accepting the connection. Reconnect and your emails send from your mailbox again.'
            : 'Emails go out from your own mailbox and show in your Sent folder. bcecloud can send for you, never read your mail.'
        }
        action={
          <Button disabled={connect.isPending} onClick={() => connect.mutate()}>
            {connect.isPending ? 'Opening Google…' : reconnect ? 'Reconnect Gmail' : 'Connect Gmail'}
          </Button>
        }
      />
    </Panel>
  );
}

interface ComposeFormProps {
  /** The assistant's draft to start from, or null for a blank email. */
  readonly draft: PendingDraft | null;
  readonly context: ComposeContext;
  readonly ownerId: string | undefined;
  readonly templates: readonly OutreachTemplate[];
  readonly placeholderLabels: ReadonlyArray<{ readonly key: string; readonly label: string; readonly sample: string }>;
  readonly canSend: boolean;
  readonly from: string | null;
}

function ComposeForm({ draft, context, ownerId, templates, placeholderLabels, canSend, from }: ComposeFormProps): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sendable = context.contacts.filter((contact) => !contact.emailOptOut);
  // The assistant's pick only when it is someone who may be emailed.
  const draftContact = draft ? sendable.find((contact) => contact.id === draft.contactId) : undefined;
  const [contactId, setContactId] = useState(draftContact?.id ?? sendable[0]?.id ?? '');
  const [templateId, setTemplateId] = useState(draft?.templateId && templates.some((t) => t.id === draft.templateId) ? draft.templateId : '');
  // The draft keeps its {placeholders}; they are filled for the chosen contact in Preview and on Send.
  const [subject, setSubject] = useState(draft?.subject ?? '');
  const [body, setBody] = useState(draft?.body ?? '');
  const [view, setView] = useState<View>(VIEW.draft);
  const [target, setTarget] = useState<TextField>(TEXT_FIELD.body);
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const signature = useQuery({ queryKey: SIGNATURE_QUERY_KEY, queryFn: ({ signal }) => readSignature(signal), staleTime: 10 * 60_000 });

  const contact = context.contacts.find((candidate) => candidate.id === contactId);
  const values = useMemo(
    () => ({ ...context.values, ...(contact?.firstName ? { [FIRST_NAME_PLACEHOLDER]: contact.firstName } : {}) }),
    [context.values, contact],
  );
  const filledSubject = fillForClient(subject, values);
  const filledBody = fillForClient(body, values);
  const unfilled = [...new Set([...filledSubject.unfilled, ...filledBody.unfilled])];
  const ready = canSend && contact !== undefined && subject.trim() !== '' && body.trim() !== '' && unfilled.length === 0;

  const pickTemplate = (id: string): void => {
    setTemplateId(id);
    const template = templates.find((candidate) => candidate.id === id);
    if (!template) return;
    setSubject(template.subject);
    setBody(template.body);
  };

  const insert = (key: string): void => {
    const token = placeholderToken(key);
    const field = target === TEXT_FIELD.subject ? subjectRef.current : bodyRef.current;
    const value = target === TEXT_FIELD.subject ? subject : body;
    const next = insertAt(value, token, { start: field?.selectionStart ?? null, end: field?.selectionEnd ?? null });
    if (target === TEXT_FIELD.subject) setSubject(next.value);
    else setBody(next.value);
    requestAnimationFrame(() => {
      field?.focus();
      field?.setSelectionRange(next.caret, next.caret);
    });
  };

  const send = useMutation({
    mutationFn: () =>
      sendOutreachEmail(context.accountId, {
        contactId,
        subject: filledSubject.text.trim(),
        body: filledBody.text.trim(),
        ...(templateId ? { templateId } : {}),
        ...(ownerId ? { ownerId } : {}),
      }),
    onSuccess: async (result) => {
      clearDraft();
      if (result.salesforceError) toast.warning(`Email sent. ${result.salesforceError}`);
      else toast.success(`Email sent to ${contact?.name || contact?.email || 'the client'}, logged to Salesforce`);
      await queryClient.invalidateQueries({ queryKey: REPEAT_CLIENTS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ['outreach-email', 'client', context.accountId] });
      navigate(REPEAT_CLIENTS_PATH);
    },
  });

  if (context.contacts.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon={<MailIcon aria-hidden className="size-6" />}
          title="No contacts with an email address"
          message="Add the client’s contact in Salesforce with an email address, and they show here after the next sync."
        />
      </Panel>
    );
  }

  return (
    <Panel className="space-y-5 p-6">
      {draft ? (
        <ErrorNotice
          inline
          tone="warning"
          message={
            draftContact
              ? 'The assistant drafted this email. Read it through and edit anything before you send.'
              : 'The assistant drafted this email for someone who can’t be emailed, so pick who it goes to before you send.'
          }
        />
      ) : null}
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="From" htmlFor="outreach-from">
          <TextInput id="outreach-from" value={from ?? 'Connect your Gmail first'} disabled readOnly />
        </Field>
        <Field label="To" htmlFor="outreach-to" required>
          <SelectInput id="outreach-to" value={contactId} onChange={(event) => setContactId(event.target.value)}>
            {context.contacts.map((candidate) => (
              <option key={candidate.id} value={candidate.id} disabled={candidate.emailOptOut}>
                {`${candidate.name || candidate.email} <${candidate.email}>`}
                {candidate.title ? `, ${candidate.title}` : ''}
                {candidate.emailOptOut ? ' (opted out of email)' : ''}
              </option>
            ))}
          </SelectInput>
        </Field>
      </div>
      <Field label="Start from a template" htmlFor="outreach-template" hint="Fills the subject and email; edit them freely after.">
        <SelectInput id="outreach-template" value={templateId} onChange={(event) => pickTemplate(event.target.value)}>
          <option value="">Write from scratch</option>
          {templates.map((template) => (
            <option key={template.id} value={template.id}>
              {template.scope === 'team' ? `Team: ${template.name}` : template.name}
              {template.whenToUse ? ` (${template.whenToUse})` : ''}
            </option>
          ))}
        </SelectInput>
      </Field>

      <Tabs
        label="Write or preview"
        active={view}
        onChange={setView}
        items={[
          { key: VIEW.draft, label: 'Draft' },
          { key: VIEW.preview, label: 'Preview' },
        ]}
      />
      {view === VIEW.preview ? (
        <TemplatePreview
          title={`As ${contact?.firstName || 'the client'} reads it`}
          subject={subject}
          body={body}
          placeholders={Object.entries(values).map(([key, sample]) => ({ key, label: key, sample }))}
          signature={signature.data}
        />
      ) : (
        <>
          <PlaceholderPicker placeholders={placeholderLabels} target={TEXT_FIELD_NAME[target]} onInsert={insert} />
          <Field label="Subject" htmlFor="outreach-subject" required>
            <TextInput
              id="outreach-subject"
              ref={subjectRef}
              maxLength={OUTREACH_TEMPLATE_LIMITS.subject}
              value={subject}
              onFocus={() => setTarget(TEXT_FIELD.subject)}
              onChange={(event) => setSubject(event.target.value)}
            />
          </Field>
          <Field label="Email" htmlFor="outreach-body" required hint="Plain text. Placeholders fill in for this contact; your Salesforce signature goes under it.">
            <TextArea
              id="outreach-body"
              ref={bodyRef}
              rows={14}
              maxLength={10_000}
              value={body}
              onFocus={() => setTarget(TEXT_FIELD.body)}
              onChange={(event) => setBody(event.target.value)}
            />
          </Field>
        </>
      )}

      {unfilled.length > 0 ? (
        <ErrorNotice
          inline
          tone="warning"
          message={`Nothing to fill ${unfilled.map(placeholderToken).join(', ')} with for this client. Write it in yourself before sending.`}
        />
      ) : null}
      {send.isError ? <ErrorNotice inline title="Email not sent" message={errorMessage(send.error)} /> : null}

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-portal-line pt-5">
        <Button variant="secondary" onClick={() => navigate(REPEAT_CLIENTS_PATH)}>
          Cancel
        </Button>
        <Button disabled={!ready || send.isPending} onClick={() => send.mutate()}>
          {send.isPending ? 'Sending…' : 'Send email'}
        </Button>
      </div>
    </Panel>
  );
}

/** Emails already sent to this client from bcecloud, with their opens. */
function History({ emails }: { readonly emails: readonly SentEmail[] }): React.ReactElement {
  return (
    <aside>
      <SectionHeader title="Emails to this client" description="Sent from bcecloud. Opens count when their mail loads images, so treat them as a hint." />
      {emails.length === 0 ? (
        <Panel>
          <EmptyState message="Nothing sent from bcecloud yet. Your first email shows here with its opens." />
        </Panel>
      ) : (
        <Panel>
          <ul className="divide-y divide-portal-line">
            {emails.map((email) => (
              <li key={email.id} className="p-4">
                <p className="text-portal-small font-bold text-portal-ink">{email.subject}</p>
                <p className="text-portal-micro text-portal-muted">
                  {SENT_AT.format(new Date(email.sentAt))} to {email.to}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Pill tone={email.opens > 0 ? 'success' : 'neutral'}>{opensLabel(email.opens)}</Pill>
                  {email.loggedToSalesforce ? null : <Pill tone="warning">Not in Salesforce</Pill>}
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </aside>
  );
}
